import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@apollo/client";
import { Plus, Search, Users } from "lucide-react";
import { Avatar, Badge, EmptyState, PageHeader, Pagination, TagChips } from "../../components/ui";
import { GET_PEOPLE } from "../../apollo/clients";
import { AddClientModal } from "./AddClientModal";
import {
  deriveStatus,
  getDisplayName,
  getSecondaryName,
  getInitials,
  formatSessionLabel,
  CLIENT_STATUS_LABEL,
  CLIENT_STATUS_COLOR,
} from "../../data/clientDerive";
import type { ListPerson, ClientStatus } from "../../data/clientDerive";

const PER_PAGE = 50;
const DEBOUNCE_MS = 300;

type FilterKey = "all" | "archived";

function StatusPill({ status }: { status: ClientStatus }) {
  const cfg = CLIENT_STATUS_COLOR[status];
  return (
    <div className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium ${cfg.text}`}>
      <div className={`dot ${cfg.dot}`} />
      {CLIENT_STATUS_LABEL[status]}
    </div>
  );
}

/**
 * The People sub-tab: the contact-level directory. Every person belongs to
 * one or more Clients (1:1 is the common case); this list manages the
 * people themselves, the Clients tab manages the relationships.
 */
export function People() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = (() => {
    const v = parseInt(searchParams.get("page") ?? "0", 10);
    return Number.isNaN(v) || v < 0 ? 0 : v;
  })();
  const filterKey: FilterKey = searchParams.get("filter") === "archived" ? "archived" : "all";
  const debouncedSearch = searchParams.get("q") ?? "";

  const [searchInput, setSearchInput] = useState(() => searchParams.get("q") ?? "");
  const [addOpen, setAddOpen] = useState(false);

  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  });

  // Debounced q → URL (page resets on change)
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchParamsRef.current((prev) => {
        if ((prev.get("q") ?? "") === trimmed) return prev;
        const next = new URLSearchParams(prev);
        if (trimmed) next.set("q", trimmed); else next.delete("q");
        next.delete("page");
        return next;
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const setPage = useCallback((p: number) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      if (p === 0) next.delete("page"); else next.set("page", String(p));
      return next;
    });
  }, []);

  const filter = useMemo(() => {
    const f: Record<string, unknown> = { archived: filterKey === "archived" };
    if (debouncedSearch) f.q = debouncedSearch;
    return f;
  }, [debouncedSearch, filterKey]);

  const { data, loading, error, previousData, refetch } = useQuery(GET_PEOPLE, {
    variables: {
      page,
      perPage: PER_PAGE,
      sortField: "createdAt",
      sortOrder: "DESC",
      filter,
    },
    fetchPolicy: "cache-and-network",
  });

  const effectiveData = data ?? previousData;
  const people: ListPerson[] = effectiveData?.allPeople ?? [];
  const totalCount: number = effectiveData?._allPeopleMeta?.count ?? 0;

  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return null;
    return formatSessionLabel(dt);
  };

  const summaryText =
    loading && people.length === 0
      ? "Loading people..."
      : totalCount > 0
        ? `${totalCount} ${totalCount === 1 ? "person" : "people"} across your clients.`
        : filterKey === "archived"
          ? "No archived people."
          : "No people yet.";

  return (
    <div>
      <PageHeader
        tabsLabel="People views"
        tabs={[
          { key: "all", label: "All people" },
          { key: "archived", label: "Archived" },
        ]}
        activeTab={filterKey}
        tabParam="filter"
        defaultTab="all"
        status={summaryText}
        controls={
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search
              size={13}
              strokeWidth={2}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4 pointer-events-none"
            />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search people by name, email, or phone"
              placeholder="Search people..."
              className="w-full pl-8 pr-3 min-h-11 md:min-h-0 md:py-2 text-[13px] rounded-md bg-paper-hi hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-4"
            />
          </div>
        }
        actions={
          <button
            onClick={() => setAddOpen(true)}
            className="px-3.5 py-2 rounded-md bg-tide text-paper-hi text-[13px] font-medium flex items-center gap-1.5 hover:bg-tide-d transition-colors"
          >
            <Plus size={13} strokeWidth={2.2} />
            Add person
          </button>
        }
        mobileAction={{
          label: "Add person",
          icon: <Plus size={16} strokeWidth={2.2} />,
          onClick: () => setAddOpen(true),
        }}
      />

      <div className="px-4 md:px-7 pb-20">
        {loading && people.length === 0 ? (
          <div className="bg-paper-hi rounded-lg shadow-card p-6">
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded bg-canvas" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 w-40 bg-canvas rounded" />
                    <div className="h-2.5 w-56 bg-canvas rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : error && people.length === 0 ? (
          <div className="bg-paper-hi rounded-lg shadow-card">
            <EmptyState
              icon={<Users size={28} strokeWidth={1.5} />}
              heading="Failed to load people"
              description="Something went wrong. Please try refreshing the page."
            />
          </div>
        ) : people.length > 0 ? (
          <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 gap-3 px-5 py-2.5 hair-b bg-wash text-[11px] font-semibold text-ink-4 uppercase tracking-wider">
              <div className="col-span-4">Name</div>
              <div className="col-span-3 hidden md:block">Clients</div>
              <div className="col-span-2 hidden md:block">Status</div>
              <div className="col-span-1 hidden lg:block">Sessions</div>
              <div className="col-span-2 hidden lg:block">Last session</div>
            </div>
            <div className="divide-hair">
              {people.map((p) => {
                const memberships = p.clients ?? [];
                return (
                  <Link
                    key={p.id}
                    to={`/clients/people/${p.id}`}
                    state={{ from: searchParams.toString() }}
                    className="grid grid-cols-12 gap-3 px-5 py-3.5 items-center hover:bg-paper-2 transition cursor-pointer"
                  >
                    <div className="col-span-4 min-w-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar initials={getInitials(p)} size="lg" />
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-medium truncate flex items-center gap-2">
                            {getDisplayName(p)}
                            {p.archivedAt && <Badge variant="default">Archived</Badge>}
                          </div>
                          <div className="text-[11.5px] truncate text-ink-4">
                            {getSecondaryName(p) ?? p.email}
                          </div>
                          {p.tags?.length > 0 && (
                            <div className="mt-1">
                              <TagChips tags={p.tags} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-span-3 hidden md:block min-w-0">
                      <div className="text-[12px] text-ink-3 truncate">
                        {memberships.length === 0
                          ? "--"
                          : memberships.map((m, i) => (
                              <span key={m.id}>
                                {i > 0 && ", "}
                                <span className={m.isPrimary ? "font-medium text-ink-2" : ""}>
                                  {m.alias || m.name}
                                </span>
                              </span>
                            ))}
                      </div>
                    </div>
                    <div className="col-span-2 hidden md:block">
                      <StatusPill status={deriveStatus(p)} />
                    </div>
                    <div className="col-span-1 hidden lg:block">
                      <span className="text-[12.5px] text-ink-3 num">{p.sessionsCount}</span>
                    </div>
                    <div className="col-span-2 hidden lg:block min-w-0">
                      <div className="text-[12.5px] text-ink-3 truncate">
                        {formatDate(p.lastSessionAt) ?? "--"}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="px-5 py-3 hair-t bg-wash text-[12px] text-ink-3">
              <Pagination
                page={page}
                perPage={PER_PAGE}
                total={totalCount}
                onPageChange={setPage}
                unit="person"
              />
            </div>
          </div>
        ) : (
          <div className="bg-paper-hi rounded-lg shadow-card">
            <EmptyState
              icon={<Users size={28} strokeWidth={1.5} />}
              heading={
                debouncedSearch
                  ? "No people match your search"
                  : filterKey === "archived"
                    ? "No archived people"
                    : "No people yet"
              }
              description={
                debouncedSearch
                  ? "Try a different name, email, or phone number."
                  : "People will appear here once your first booking is made."
              }
            />
          </div>
        )}
      </div>

      {/* Standalone add: creates the person plus a 1:1 client around them */}
      <AddClientModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => refetch()}
      />
    </div>
  );
}

